import type { FC } from "react";

import { cn } from "@/lib/utils";
import { m } from "@recobra/i18n/messages";
import type { EditableTaskStatus, Task, TaskStatus } from "@recobra/schemas/task";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@recobra/ui/select";

import { useTaskTypes } from "../task-data";
import { TASK_STATUS_CONFIG } from "./task-status-config";
import {
  decodeTaskStatusValue,
  encodeTaskStatusValue,
  SELECTABLE_TASK_STATUSES,
  taskStatusValueLabel,
} from "./taskStatusOptions";

export type TaskStatusSelectTask = Pick<Task, "type" | "status" | "waitingStatus">;

export const TaskStatusSelect: FC<{
  task: TaskStatusSelectTask;
  disabled: boolean;
  className?: string;
  onChange: (status: EditableTaskStatus, waitingStatus?: string) => void;
}> = ({ task, disabled, className, onChange }) => {
  const value = encodeTaskStatusValue(task.status, task.waitingStatus);
  const types = useTaskTypes();
  const waitingStatuses = types.data?.get(task.type)?.waitingStatuses ?? [];

  return (
    <Select
      value={value}
      onValueChange={(nextValue) => {
        if (nextValue === value) return;
        const decoded = decodeTaskStatusValue(nextValue);
        if (decoded && decoded.status !== "canceled")
          onChange(decoded.status, decoded.waitingStatus);
      }}
      itemToStringLabel={taskStatusValueLabel}
      disabled={disabled}
    >
      <SelectTrigger size="sm" className={className} aria-label={m.task_field_status()}>
        <TaskStatusIcon status={task.status} />
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end" alignItemWithTrigger={false}>
        <SelectGroup>
          {SELECTABLE_TASK_STATUSES.map((status) => (
            <SelectItem key={status} value={encodeTaskStatusValue(status)}>
              <TaskStatusIcon status={status} />
              {m.task_status_label({ status })}
            </SelectItem>
          ))}
        </SelectGroup>
        {waitingStatuses.length > 0 ? (
          <SelectGroup>
            <SelectLabel>{m.task_status_label({ status: "waiting" })}</SelectLabel>
            {waitingStatuses.map((waitingStatus) => (
              <SelectItem
                key={waitingStatus}
                value={encodeTaskStatusValue("waiting", waitingStatus)}
              >
                <TaskStatusIcon status="waiting" />
                {m.task_waiting_status_label({ waitingStatus })}
              </SelectItem>
            ))}
          </SelectGroup>
        ) : null}
      </SelectContent>
    </Select>
  );
};

/** Colour lives on the icon, GitHub-style, so the label keeps full contrast. */
export const TaskStatusIcon: FC<{ status: TaskStatus; className?: string }> = ({
  status,
  className,
}) => {
  const { icon: Icon, textClassName } = TASK_STATUS_CONFIG[status];

  return <Icon className={cn("size-4 shrink-0 self-center", textClassName, className)} />;
};
